import React, { useMemo, useState } from 'react';
import { navigate, RouteComponentProps } from '@reach/router';
import { styled } from '@mui/material/styles';
import {
  Box,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { TextView, Button } from '@atoms';
import FileUploader from './FileUploader';
import HtmlTextInput from './HtmlTextInput';
import HtmlPreview from './HtmlPreview';
import ReportControls from './ReportControls';
import { processHtmlContent } from './processHtmlContent';

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%'
}));

const DEFAULT_TITLE = 'SalesRook Report';
const DEFAULT_DESCRIPTION =
  'SalesRook - Advanced sales analytics and reporting platform for data-driven business growth';

const CreateArtifact: React.FC<RouteComponentProps> = () => {
  const [htmlContent, setHtmlContent] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [includeHeaderFooter, setIncludeHeaderFooter] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [inputDialogOpen, setInputDialogOpen] = useState(false);

  const previewHtmlContent = useMemo(() => {
    if (!htmlContent) return '';
    const shareUrl = `${process.env.REACT_APP_API_BASE}/a/preview`;
    return processHtmlContent(
      htmlContent,
      shareUrl,
      title || DEFAULT_TITLE,
      description || DEFAULT_DESCRIPTION,
      includeHeaderFooter
    );
  }, [htmlContent, title, description, includeHeaderFooter]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleHtmlContentChange = (content: string) => {
    setHtmlContent(content);
    setInputDialogOpen(false);
  };

  const handleBack = () => {
    navigate('/artifacts');
  };

  return (
    <StyledContainer maxWidth="xl" sx={{ maxHeight: '100vh' }}>
      {/* Compact header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap'
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleBack} size="small" sx={{ bgcolor: '#f5f5f5' }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <TextView variant="h5" $fontWeight={600}>
            Create New Artifact
          </TextView>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => setInputDialogOpen(true)}
            sx={{ height: 36 }}>
            {htmlContent ? 'Replace HTML' : 'Add HTML'}
          </Button>
          <ReportControls
            htmlContent={htmlContent}
            title={title}
            description={description}
            includeHeaderFooter={includeHeaderFooter}
            onTitleChange={setTitle}
            onDescriptionChange={setDescription}
            onIncludeHeaderFooterChange={setIncludeHeaderFooter}
            compact
          />
        </Box>
      </Box>

      {/* Full-width preview */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: 'calc(100vh - 110px)'
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <TextView variant="subtitle2" $fontWeight={600} color="text.secondary">
            Preview
          </TextView>
        </Box>
        <HtmlPreview htmlContent={previewHtmlContent} />
      </Box>

      {/* Add/Replace HTML dialog */}
      <Dialog
        open={inputDialogOpen}
        onClose={() => setInputDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { minHeight: 360 } }}>
        <DialogTitle>Add HTML Content</DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{ mb: 2 }}
            TabIndicatorProps={{ sx: { height: 2 } }}>
            <Tab label="Upload file" />
            <Tab label="Paste HTML" />
          </Tabs>
          {activeTab === 0 ? (
            <FileUploader onHtmlContent={handleHtmlContentChange} />
          ) : (
            <HtmlTextInput onHtmlContent={handleHtmlContentChange} />
          )}
        </DialogContent>
      </Dialog>
    </StyledContainer>
  );
};

export default CreateArtifact;
